import React, { useMemo } from 'react'

import CloseIcon from '@mui/icons-material/Close'
import { Box, Grid, IconButton } from '@mui/material'
import { GridEventListener } from '@mui/x-data-grid'
import { format, parseJSON } from 'date-fns'
import { useForm } from 'react-hook-form'
import { useDebounce } from 'use-debounce'

import { ControlledTextField, FplDataGrid, IGridColumn } from 'components/common'
import { DATE_TIME_FORMAT, DEBOUNCE_DELAY } from 'constants/index'
import { AppSettingFilterInput } from 'generated/graphql'
import { GET_PAGED_APP_SETTINGS_QUERY } from 'graphql/queries'

import ViewAppSetting from './ViewAppSetting'

const columns: IGridColumn[] = [
  { field: 'id', headerName: 'ID', width: 100 },
  { field: 'displayName', headerName: 'Name', minWidth: 320, flex: 1, type: 'string' },
  { field: 'value', headerName: 'Value', minWidth: 150, flex: 0.5, type: 'string' },
  {
    field: 'lastModifiedBy',
    headerName: 'Last Modified By',
    minWidth: 150,
    flex: 0.5,
    type: 'string',
    valueFormatter: ({ value }) => value || 'System',
  },
  {
    field: 'lastModifiedAt',
    headerName: 'Last Modified At',
    minWidth: 150,
    flex: 0.5,
    valueFormatter: ({ value }) =>
      (value && format(parseJSON(value as string), DATE_TIME_FORMAT)) || 'N/A',
  },
]

const AppSettings = () => {
  const [selectedAppSettingId, setSelectedAppSettingId] = React.useState<number | null>(null)
  const [openColumnValuesDialog, setOpenColumnValuesDialog] = React.useState(false)

  const { control, setValue, watch } = useForm({
    shouldUnregister: true,
    defaultValues: { search: '' },
  })

  const [searchInput] = useDebounce(watch('search', ''), DEBOUNCE_DELAY)
  const searchValue = searchInput.length >= 3 ? searchInput : ''

  const handleRowClick: GridEventListener<'rowClick'> = (params) => {
    setSelectedAppSettingId(Number(params.row.id))
    setOpenColumnValuesDialog(true)
  }

  const handleCloseColumnValuesDialog = () => {
    setOpenColumnValuesDialog(false)
  }

  const handleSearchClearClick = () => {
    setValue('search', '')
  }

  const filter: AppSettingFilterInput = useMemo(
    () => ({ displayName: { contains: searchValue } }),
    [searchValue],
  )

  return (
    <>
      <FplDataGrid
        query={GET_PAGED_APP_SETTINGS_QUERY}
        entityName='appSettings'
        columns={columns}
        filter={filter}
        defaultOrder={{ field: 'id', sort: 'asc' }}
        toolbar={{
          caption: 'Global Variables',
          rightSide: (
            <Grid item>
              <Box width='210px'>
                <ControlledTextField
                  control={control}
                  name='search'
                  label='Search'
                  defaultValue=''
                  size='small'
                  endAdornment={
                    searchInput && (
                      <IconButton size='small' onClick={handleSearchClearClick}>
                        <CloseIcon fontSize='small' />
                      </IconButton>
                    )
                  }
                />
              </Box>
            </Grid>
          ),
        }}
        onRowClick={handleRowClick}
      />

      {openColumnValuesDialog && (
        <ViewAppSetting
          selectedAppSettingId={selectedAppSettingId}
          openDialog={openColumnValuesDialog}
          handleCloseDialog={handleCloseColumnValuesDialog}
        />
      )}
    </>
  )
}

export default AppSettings
